<template>
  <OareContentView title="Comments">
    <v-container class="pa-0">
      <v-row>
        <v-col cols="2">
          <div class="mt-6">
            <h3>Filter</h3>
            <v-select
              label="Status"
              :items="statusOptions"
              v-model="status"
              clearable
              class="pt-2 test-status-filter"
              density="compact"
            />
            <v-text-field
              v-model="name"
              label="Thread"
              single-line
              hide-details
              clearable
              class="pt-0 pb-2 test-name-filter"
            />
            <v-text-field
              v-model="item"
              label="Item"
              single-line
              hide-details
              clearable
              class="pt-2 pb-2 test-item-filter"
            />
            <v-text-field
              v-model="comment"
              label="Comment"
              single-line
              hide-details
              clearable
              class="pb-2 test-comment-filter"
            />
          </div>
        </v-col>
        <v-col cols="10" class="pl-8">
          <oare-data-table
            :headers="tableHeaders"
            class="mt-3 table-cursor"
            item-value="uuid"
            defaultSort="timestamp"
            :items="threadDisplays"
            :items-length="serverCount"
            :fetchItems="getAllThreadsWithComments"
            :watched-params="['name', 'item', 'comment', 'status']"
            debounce
          >
            <template #[`item.status`]="{ item }">
              {{ item.thread.status }}
            </template>
            <template #[`item.name`]="{ item }">
              <v-btn
                @click="setupThreadDialog(item)"
                elevation="0"
                class="font-weight-bold ml-n3 test-thread-name"
                color="primary"
                variant="plain"
                size="small"
              >
                {{ item.thread.name || 'Untitled' }}</v-btn
              >
            </template>
            <template #[`item.item`]="{ item }">
              {{ item.word }}
            </template>
            <template #[`item.comments`]="{ item }">
              <div class="pt-2 pb-2">
                <div v-for="(comment, idx) in getItemComments(item)" :key="idx">
                  <span v-if="idx <= 2">{{
                    formatCommentText(idx, comment.text)
                  }}</span>
                </div>
                <div
                  v-if="item.comments.length > 3"
                  @click="setSelectedComments(item.comments)"
                  class="test-view-all-comments"
                >
                  <v-btn
                    color="primary"
                    elevation="0"
                    class="font-weight-bold ml-n3"
                    variant="plain"
                    size="small"
                    >See more...</v-btn
                  >
                </div>
              </div>
            </template>
            <template #[`item.timestamp`]="{ item }">
              {{ formatTimestamp(item.latestCommentDate) }}
            </template>
          </oare-data-table>
        </v-col>
      </v-row>
    </v-container>

    <oare-dialog
      v-if="selectedComments.length !== 0"
      title="All Comments"
      :model-value="selectedComments.length !== 0"
      :width="500"
      :closeButton="true"
      :persistent="false"
      :show-cancel="false"
      :show-submit="false"
      @update:model-value="setSelectedComments([])"
      class="test-view-all-comments-dialog"
    >
      <div
        v-for="(comment, idx) in selectedComments"
        :key="idx"
        class="test-comment"
      >
        {{ formatCommentText(idx, comment.text, true) }}
      </div>
    </oare-dialog>
    <comment-item-display
      v-if="isViewingThread"
      :route="selectedThreadDisplay.thread.route"
      :uuid="selectedThreadDisplay.thread.referenceUuid"
      :item="selectedThreadDisplay.word"
      :initial-thread-uuid="selectedThreadDisplay.thread.uuid"
      :showDictionary="
        !!selectedThreadDisplay.thread.route &&
        selectedThreadDisplay.thread.route.includes('dictionaryWord')
      "
      :showEpigraphy="
        !!selectedThreadDisplay.thread.route &&
        selectedThreadDisplay.thread.route.includes('epigraphies')
      "
      @submit="isViewingThread = false"
      @update:model-value="isViewingThread = false"
      class="test-comment-word-display"
    >
      {{ selectedThreadDisplay.word }}
    </comment-item-display>
  </OareContentView>
</template>

<script lang="ts">
import { defineComponent, Ref, ref } from 'vue';
import {
  ThreadDisplay,
  Comment,
  AllCommentsRequest,
  CommentSortType,
  ThreadStatus,
} from '@oare/types';
import sl from '@/serviceLocator';
import { DataTableHeader } from 'vuetify';
import useQueryParam from '@/hooks/useQueryParam';
import CommentItemDisplay from '@/components/CommentItemDisplay/index.vue';
import { formatTimestamp } from '@/utils';
import { OareDataTableOptions } from '@/components/base/OareDataTable.vue';

export default defineComponent({
  name: 'CommentView',
  components: { CommentItemDisplay },
  props: {
    isUserComments: {
      type: Boolean,
      default: true,
    },
  },

  setup({ isUserComments }) {
    const isViewingThread = ref(false);
    const threadDisplays: Ref<ThreadDisplay[]> = ref([]);
    const server = sl.get('serverProxy');
    const actions = sl.get('globalActions');
    const serverCount = ref(0);
    const selectedComments: Ref<Comment[]> = ref([]);
    const selectedThreadDisplay = ref<ThreadDisplay>({
      thread: {
        uuid: '',
        name: null,
        referenceUuid: '',
        status: 'New',
        route: '',
      },
      word: '',
      latestCommentDate: new Date(),
      comments: [],
    });
    const status = useQueryParam('status', '', true);
    const name = useQueryParam('name', '', true);
    const item = useQueryParam('item', '', true);
    const comment = useQueryParam('comment', '', true);
    const statusOptions: string[] = [
      'New',
      'In Progress',
      'Pending',
      'Completed',
    ];
    const tableHeaders: Ref<DataTableHeader[]> = ref([
      { title: 'Status', key: 'status', width: '10%' },
      { title: 'Thread', key: 'name', width: '20%' },
      { title: 'Item', key: 'item', width: '15%' },
      { title: 'Comments', key: 'comments', width: '40%', sortable: false },
      { title: 'Timestamp', key: 'timestamp', width: '15%' },
    ]);

    const getAllThreadsWithComments = async ({
      page,
      rows,
      sortBy,
      sortDesc,
    }: OareDataTableOptions) => {
      try {
        const request: AllCommentsRequest = {
          filters: {
            status: status.value
              ? (status.value as ThreadStatus)
              : ('All' as ThreadStatus),
            thread: name.value,
            item: item.value,
            comment: comment.value,
          },
          sort: {
            type: sortBy as CommentSortType,
            desc: sortDesc,
          },
          pagination: {
            page,
            limit: rows,
          },
          isUserComments,
        };

        const response = await server.getAllThreads(request);
        threadDisplays.value = response.threads;
        serverCount.value = response.count;
      } catch (err) {
        actions.showErrorSnackbar('Failed to get threads', err as Error);
      }
    };

    const formatCommentText = (idx: number, text: string, extended = false) => {
      if (extended) {
        return idx + 1 + '. ' + text;
      }

      return (
        idx +
        1 +
        '. ' +
        (text.length > 80 ? text.substr(0, 80) + '. . .' : text)
      );
    };

    const setSelectedComments = (comments: Comment[]) => {
      selectedComments.value = comments;
    };

    const setupThreadDialog = (threadDisplay: ThreadDisplay) => {
      isViewingThread.value = true;
      selectedThreadDisplay.value = threadDisplay;
    };

    const getItemComments = (item: ThreadDisplay) => {
      return item.comments;
    };

    return {
      setupThreadDialog,
      selectedThreadDisplay,
      isViewingThread,
      status,
      name,
      item,
      comment,
      statusOptions,
      threadDisplays,
      serverCount,
      tableHeaders,
      formatTimestamp,
      formatCommentText,
      selectedComments,
      setSelectedComments,
      getAllThreadsWithComments,
      getItemComments,
    };
  },
});
</script>
